/**
 * useWorkflow Hook
 * Manages workflow designer state and operations
 */

import { useState, useCallback, useRef } from 'react'
import { useApp } from '../context'
import { WorkflowExecutor, ExecutionEvents, createExecutionContext } from '../services/workflowExecutor'

export function useWorkflow(applicationId) {
  const { db, api } = useApp()
  const [workflow, setWorkflow] = useState(null)
  const [workflows, setWorkflows] = useState([])
  const [executing, setExecuting] = useState(false)
  const [paused, setPaused] = useState(false)
  const [pausedAtNode, setPausedAtNode] = useState(null)
  const [executionResults, setExecutionResults] = useState(null)
  const [executionProgress, setExecutionProgress] = useState({})
  const [executionLogs, setExecutionLogs] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const executorRef = useRef(null)

  // Add a log entry
  const addLog = useCallback((message, level = 'info', node = null) => {
    setExecutionLogs(prev => [...prev, {
      timestamp: new Date(),
      message,
      level,
      node,
    }])
  }, [])

  // Clear execution logs
  const clearLogs = useCallback(() => {
    setExecutionLogs([])
  }, [])

  // Load all workflows for an application
  const loadWorkflows = useCallback(async (appId = applicationId) => {
    if (!appId) return
    setLoading(true)
    setError(null)
    try {
      const list = await db.getWorkflows(appId)
      setWorkflows(list)
    } catch (err) {
      setError(err.message)
      console.error('Failed to load workflows:', err)
    } finally {
      setLoading(false)
    }
  }, [db, applicationId])

  // Load a specific workflow
  const loadWorkflow = useCallback(async (workflowId) => {
    setLoading(true)
    setError(null)
    try {
      const wf = await db.getWorkflow(workflowId)
      setWorkflow(wf)
      return wf
    } catch (err) {
      setError(err.message)
      console.error('Failed to load workflow:', err)
      return null
    } finally {
      setLoading(false)
    }
  }, [db])

  // Create a new workflow
  const createWorkflow = useCallback(async (data) => {
    setError(null)
    try {
      const wf = await db.createWorkflow({
        ...data,
        applicationId,
      })
      setWorkflows(prev => [...prev, wf])
      setWorkflow(wf)
      return wf
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, applicationId])

  // Save/update current workflow
  const saveWorkflow = useCallback(async (data = {}) => {
    if (!workflow) {
      // Create new if none exists
      return createWorkflow(data)
    }

    setSaving(true)
    setError(null)
    try {
      const wf = await db.updateWorkflow(workflow.id, {
        ...data,
        nodes: data.nodes || workflow.nodes,
        edges: data.edges || workflow.edges,
      })
      setWorkflow(wf)
      setWorkflows(prev => prev.map(w => w.id === wf.id ? wf : w))
      return wf
    } catch (err) {
      setError(err.message)
      throw err
    } finally {
      setSaving(false)
    }
  }, [db, workflow, createWorkflow])

  // Update workflow nodes
  const updateNodes = useCallback((nodes) => {
    if (workflow) {
      setWorkflow(prev => ({
        ...prev,
        nodes,
      }))
    }
  }, [workflow])

  // Update workflow edges
  const updateEdges = useCallback((edges) => {
    if (workflow) {
      setWorkflow(prev => ({
        ...prev,
        edges,
      }))
    }
  }, [workflow])

  // Add a node
  const addNode = useCallback((node) => {
    setWorkflow(prev => {
      if (!prev) return prev
      return {
        ...prev,
        nodes: [...prev.nodes, node],
      }
    })
  }, [])

  // Remove a node
  const removeNode = useCallback((nodeId) => {
    setWorkflow(prev => {
      if (!prev) return prev
      return {
        ...prev,
        nodes: prev.nodes.filter(n => n.id !== nodeId),
        edges: prev.edges.filter(e => e.source !== nodeId && e.target !== nodeId),
      }
    })
  }, [])

  // Add an edge
  const addEdge = useCallback((edge) => {
    setWorkflow(prev => {
      if (!prev) return prev
      return {
        ...prev,
        edges: [...prev.edges, edge],
      }
    })
  }, [])

  // Remove an edge
  const removeEdge = useCallback((edgeId) => {
    setWorkflow(prev => {
      if (!prev) return prev
      return {
        ...prev,
        edges: prev.edges.filter(e => e.id !== edgeId),
      }
    })
  }, [])

  // Delete a workflow
  const deleteWorkflow = useCallback(async (workflowId) => {
    setError(null)
    try {
      await db.deleteWorkflow(workflowId)
      setWorkflows(prev => prev.filter(w => w.id !== workflowId))
      if (workflow?.id === workflowId) {
        setWorkflow(null)
      }
    } catch (err) {
      setError(err.message)
      throw err
    }
  }, [db, workflow])

  // Execute the workflow using WorkflowExecutor
  // workflowData parameter allows passing canvas nodes/edges directly
  const executeWorkflow = useCallback(async (account, application, apiBaseUrl = 'http://localhost:8000', workflowData = null) => {
    // Use provided workflow data or fall back to stored workflow
    const workflowToExecute = workflowData || workflow

    if (!workflowToExecute || !workflowToExecute.nodes || workflowToExecute.nodes.length === 0) {
      setError('No workflow nodes to execute')
      return null
    }

    if (!account?.account_id || !application?.app_name) {
      setError('Account and application must be selected to run workflow')
      return null
    }

    setExecuting(true)
    setExecutionResults(null)
    setExecutionProgress({})
    setError(null)

    // Helper to get node label from ID
    const getNodeLabel = (nodeId) => {
      const node = workflowToExecute.nodes.find(n => n.id === nodeId)
      return node?.data?.label || nodeId
    }

    try {
      // Get user_id from localStorage (REQUIRED - 2026-01-14)
      const userStr = localStorage.getItem('modernizeit_user')
      let userId = null
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          userId = user.user_id
        } catch (e) {
          console.error('Failed to parse user data:', e)
        }
      }

      if (!userId) {
        setError('User not authenticated. Please login again.')
        return null
      }

      // Create execution context
      const context = createExecutionContext(account, application, apiBaseUrl, userId)

      // Log workflow start
      addLog(`Workflow execution started`, 'start')
      addLog(`Account: ${account.account_id} | Application: ${application.app_name}`, 'info')
      addLog(`Nodes to execute: ${workflowToExecute.nodes.length}`, 'info')

      // Create workflow executor
      const executor = new WorkflowExecutor(workflowToExecute, context, {
        continueOnError: false,
      })
      executorRef.current = executor

      // Subscribe to execution events
      executor.on(ExecutionEvents.NODE_START, ({ nodeId, nodeType }) => {
        const label = getNodeLabel(nodeId)
        addLog(`Starting execution...`, 'start', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: { status: 'running', progress: 0, message: 'Starting...' }
        }))
      })

      executor.on(ExecutionEvents.NODE_PROGRESS, ({ nodeId, progress, message }) => {
        const label = getNodeLabel(nodeId)
        addLog(message || `Progress: ${progress}%`, 'progress', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: { status: 'running', progress, message }
        }))
      })

      executor.on(ExecutionEvents.NODE_COMPLETE, ({ nodeId, result }) => {
        const label = getNodeLabel(nodeId)
        addLog(`Completed successfully`, 'complete', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: { status: 'completed', progress: 100, message: 'Complete', result }
        }))
      })

      executor.on(ExecutionEvents.NODE_ERROR, ({ nodeId, error, nodeResult }) => {
        const label = getNodeLabel(nodeId)
        addLog(`Error: ${error}`, 'error', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: { status: 'error', progress: 0, message: error, result: nodeResult || null }
        }))
      })

      executor.on(ExecutionEvents.NODE_SKIPPED, ({ nodeId, reason }) => {
        const label = getNodeLabel(nodeId)
        addLog(`Skipped: ${reason}`, 'warning', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: { status: 'skipped', progress: 0, message: reason }
        }))
      })

      executor.on(ExecutionEvents.WORKFLOW_PAUSED, ({ pausedAtNode, reason }) => {
        const label = pausedAtNode ? getNodeLabel(pausedAtNode) : null
        const message = reason === 'human-approval'
          ? 'Workflow paused for human approval'
          : 'Workflow paused'
        addLog(message, 'pause', label)
        setPaused(true)
        setPausedAtNode(pausedAtNode)
        if (pausedAtNode) {
          setExecutionProgress(prev => ({
            ...prev,
            [pausedAtNode]: {
              ...prev[pausedAtNode],  // Preserve existing fields (especially 'result' from NODE_COMPLETE)
              status: 'paused',
              message: 'Waiting for approval...'
            }
          }))
        }
      })

      executor.on(ExecutionEvents.WORKFLOW_RESUMED, () => {
        addLog('Workflow resumed', 'info')
        setPaused(false)
        setPausedAtNode(null)
      })

      executor.on(ExecutionEvents.NODE_PAUSED, ({ nodeId, message }) => {
        const label = getNodeLabel(nodeId)
        addLog(message || 'Paused for approval', 'pause', label)
        setExecutionProgress(prev => ({
          ...prev,
          [nodeId]: {
            ...prev[nodeId],  // Preserve existing fields (especially 'result' from NODE_COMPLETE)
            status: 'paused',
            message: message || 'Waiting for approval...'
          }
        }))
      })

      // Execute workflow
      const results = await executor.execute()
      addLog(`Workflow completed successfully`, 'complete')
      setExecutionResults(results)
      executorRef.current = null

      // Clear pause state on completion
      setPaused(false)
      setPausedAtNode(null)

      return results

    } catch (err) {
      addLog(`Workflow failed: ${err.message}`, 'error')
      setError(err.message)
      executorRef.current = null

      // Clear pause state on error
      setPaused(false)
      setPausedAtNode(null)

      throw err
    } finally {
      setExecuting(false)
    }
  }, [workflow, addLog])

  // Abort current workflow execution
  const abortExecution = useCallback(() => {
    if (executorRef.current) {
      executorRef.current.abort()
      setPaused(false)
      setPausedAtNode(null)
    }
  }, [])

  // Pause current workflow execution
  const pauseExecution = useCallback(() => {
    if (executorRef.current && !executorRef.current.isPaused) {
      executorRef.current.pause()
    }
  }, [])

  // Resume current workflow execution
  const resumeExecution = useCallback(() => {
    if (executorRef.current && executorRef.current.isPaused) {
      executorRef.current.resume()
    }
  }, [])

  // Initialize empty workflow
  const initializeWorkflow = useCallback((name = 'New Workflow') => {
    setWorkflow({
      id: null,
      name,
      description: '',
      applicationId,
      nodes: [],
      edges: [],
      status: 'draft',
    })
  }, [applicationId])

  return {
    // State
    workflow,
    currentWorkflow: workflow, // Alias for backward compatibility
    workflows,
    nodes: workflow?.nodes || [],
    edges: workflow?.edges || [],
    executing,
    paused,
    pausedAtNode,
    executionResults,
    executionProgress,
    executionLogs,
    loading,
    saving,
    error,

    // Actions
    loadWorkflows,
    loadWorkflow,
    createWorkflow,
    saveWorkflow,
    deleteWorkflow,
    updateNodes,
    updateWorkflowNodes: updateNodes, // Alias
    updateEdges,
    updateWorkflowEdges: updateEdges, // Alias
    addNode,
    removeNode,
    addEdge,
    removeEdge,
    executeWorkflow,
    abortExecution,
    pauseExecution,
    resumeExecution,
    addLog,
    clearLogs,
    initializeWorkflow,
  }
}

export default useWorkflow
